import React, { useState, useEffect, useRef } from 'react';
import { Typography, Button, Box, Grid,  Container, Link, SvgIcon } from '@mui/material';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLocation, useNavigate } from 'react-router-dom';
import Login from './Login';
import { useAuth } from '../contexts/AuthContext';
import { redeemReferralCode } from '../services/api';
import { captureAttributionFromCurrentUrl, searchWithStoredAttribution } from '../services/analyticsService';
import { reopenCookieChoicesWithReloadWarning } from '../services/reloadOnConsentWithdrawal';
import { colors, commonStyles } from '../styles';

// Custom X (Twitter) Logo
const XIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </SvgIcon>
);

// Bitcoin Logo
const BitcoinIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.548v-.002zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.974.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.18-.24.45-.614.35.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.236-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084v.006z" />
  </SvgIcon>
);

const MainButton = styled(Button)(commonStyles.mainButton);
const FeatureSection = styled(Box)(commonStyles.featureSection);
const SlantedSection = styled(Box)(commonStyles.slantedSection);

const InvertedSlantedSection = styled(Box)(({ theme }) => ({
  ...commonStyles.slantedSection,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: `linear-gradient(135deg, transparent 0%, ${colors.primary} 0%)`,
    clipPath: 'polygon(0 10%, 100% 0%, 100% 100%, 0% 100%)',
    zIndex: -1,
  }
}));

const DictionaryEntry = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  padding: theme.spacing(4),
  color: colors.secondary,
}));

const Word = styled(Typography)(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 700,
  color: colors.primary,
  marginBottom: theme.spacing(1),
}));

const Pronunciation = styled('span')(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 400,
  color: colors.secondary,
  marginLeft: theme.spacing(2),
}));

const Definition = styled(Box)(({ theme }) => ({
  marginLeft: 0,
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    marginLeft: theme.spacing(4)
  }
}));

const DefinitionNumber = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const DefinitionText = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  marginBottom: theme.spacing(1),
}));

const Example = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const Origin = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${colors.primary}20`,
  paddingTop: theme.spacing(3),
  marginTop: theme.spacing(4),
}));

const DeviceMockup = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '360px',
  height: 'auto',
  margin: '0 auto',
  display: 'block',
  filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))',
}));

const FeatureBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  opacity: 0.05,
  overflow: 'hidden',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    background: colors.primary,
  },
  '&::before': {
    width: '40%',
    paddingBottom: '40%',
    top: '-10%',
    right: '-10%',
    opacity: 0.5,
  },
  '&::after': {
    width: '25%',
    paddingBottom: '25%',
    bottom: '-5%',
    left: '-5%',
    opacity: 0.3,
  },
}));


const HeroVideo = styled('video')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '100%',
  minHeight: '150vh',
  width: 'auto',
  height: 'auto',
  objectFit: 'cover',
  zIndex: 0,
});

const HeroOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '150vh',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1,
});

const LandingCtas = ({ tone, onWebSignup, onStoreDownload }) => {
  const onDark = tone === 'onDark';
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      width: '100%',
      maxWidth: 360,
    }}>
      <MainButton
        onClick={onWebSignup}
        size="large"
        sx={{ color: colors.white, width: '100%' }}
        disableRipple
      >
        GET STARTED
      </MainButton>
        <Button
          onClick={onStoreDownload}
          disableRipple
          sx={{
            color: onDark ? colors.white : colors.secondary,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              opacity: 0.85,
            },
            '&.Mui-focusVisible, &:focus-visible': {
              outline: `2px solid ${onDark ? colors.white : colors.secondary}`,
              outlineOffset: '3px',
            },
          }}
        >
        Download the app
      </Button>
    </Box>
  );
};

const Welcome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // #1390 B1: open synchronously so Login mounts with open=true.
  // Async effect-only open loses OTP deep links — Login's mount autofill
  // seeds email/otp then its !open reset clears them in the same flush.
  const [isLoginOpen, setIsLoginOpen] = useState(() => Boolean(location.state?.openAuth));
  const { user } = useAuth();
  const learnMoreRef = useRef(null);
  const [showFloatingQR, setShowFloatingQR] = useState(false);
  const [pendingReferralCode, setPendingReferralCode] = useState(null);

  // Later navigations that set openAuth (and clear sticky history state so
  // refresh/back does not re-open the modal — #1390 S2).
  // #1395 S5: use window.location.search (not the router snapshot). Login
  // strips email/otp via window.history.replaceState, which React Router
  // never observes — location.search would re-push credentials into the bar.
  useEffect(() => {
    if (location.state?.openAuth) {
      setIsLoginOpen(true);
      navigate(
        { pathname: '.', search: window.location.search },
        { replace: true, state: {} }
      );
    }
  }, [location.state, navigate]);

  // Check for pending referral code on mount
  useEffect(() => {
    const code = sessionStorage.getItem('pendingReferralCode');
    setPendingReferralCode(code);
  }, []);

  // Auto-redeem referral code right after login
  useEffect(() => {
    const handler = async () => {
      const code = sessionStorage.getItem('pendingReferralCode');
      if (!code) return;
      try {
        await redeemReferralCode(code);
      } catch (_) {
        // ignore errors (already redeemed, invalid, etc.)
      } finally {
        sessionStorage.removeItem('pendingReferralCode');
        setPendingReferralCode(null);
      }
    };
    window.addEventListener('auth:login', handler);
    return () => window.removeEventListener('auth:login', handler);
  }, []);

  // Keep the scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (learnMoreRef.current) {
        const scrollPosition = window.scrollY;
        const opacity = Math.max(0, 1 - scrollPosition / 300);
        learnMoreRef.current.style.opacity = opacity.toString();
        learnMoreRef.current.style.transform = `translate(-50%, ${scrollPosition * 0.5}px)`;
      }
      
      const isNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
      setShowFloatingQR(window.scrollY > window.innerHeight && !isNearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Same-page sign-up. Leaving for /download (the old phone "GET STARTED"
  // path) dropped the query string, so a tagged ad click never reached a
  // web user record. Capture here as well as App.js: first-touch wins, so
  // this does not replace an earlier visit.
  const openWebSignup = () => {
    captureAttributionFromCurrentUrl({ captureMethod: 'app_init' });
    setIsLoginOpen(true);
  };

  const openStoreDownload = () => {
    captureAttributionFromCurrentUrl({ captureMethod: 'app_init' });
    const search = searchWithStoredAttribution(window.location.search);
    const query = search.startsWith('?') || search === '' ? search : `?${search}`;
    // Full document load. A client-side visit leaves storeNavigation's
    // one-shot redirect claimed, so a second tap renders a blank /download.
    window.location.assign(`/download${query}`);
  };

  const features = [
    {
      id: 'gain-confidence',
      title: 'Gain confidence',
      description: (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          position: 'relative',
          zIndex: 1,
          maxWidth: '600px'
        }}>
          <Typography variant="body1" sx={{
            color: colors.secondary,
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.6,
          }}>
            Easily track your spending by day, week, and month with our intuitive dashboard. See your financial patterns at a glance and understand where your money is going in real-time.
          </Typography>
          <Typography variant="body1" sx={{
            color: colors.secondary,
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.6,
          }}>
            As you begin tracking your individual transactions, Budgit will begin to take over that job for you. Our AI learns your preferences and automatically categorizes new transactions, making financial management effortless and giving you confidence in your spending decisions.
          </Typography>
        </Box>
      ),
      mockup: '/images/mockup-cash-entry.png',
      mockupAlt: 'Budgit cash entry screen',
    },
    {
      id: 'get-rewarded',
      title: 'Get rewarded',
      description: (
        <Typography variant="body1" sx={{
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: { xs: '1rem', md: '1.1rem' },
          lineHeight: 1.6,
        }}>
          Join our community of smart savers and earn money for sticking with your budget, making smart financial decisions, and achieving your goals. Monthly rewards are paid out to the top 100 users based on your financial ambition, progress, and health. We will keep track of leading users on our leaderboard.
        </Typography>
      ),
      mockup: '/images/mockup-discover.png',
      mockupAlt: 'Budgit discover and leaderboard screen',
    },
    {
      id: 'ai-budgeting',
      title: 'Smart budgeting',
      description: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body1" sx={{
            color: colors.secondary,
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.6,
          }}>
            Your personal AI model learns from your spending patterns to provide insights and recommendations as you go. Get ahead of your finances with intelligent categorization and predictive analysis that helps you save more and stress less.
          </Typography>
          <Typography variant="body1" sx={{
            color: colors.secondary,
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.6,
          }}>
            Fine-tune your personalized AI model to match your preferences, ensuring your expenses are organized exactly how you want them. No more spending hours each week undoing and correcting mistakes - it happens while you spend. Your model improves from your corrections and preferences, while we protect your data with industry-standard security measures and anonymize or pseudonymize personal data where feasible.
          </Typography>
        </Box>
      ),
      mockup: '/images/mockup-activity.png',
      mockupAlt: 'Budgit smart budgeting screen with category tracking',
    },
    {
      id: 'bitcoin',
      title: 'Bitcoin',
      description: (
        <Typography variant="body1" sx={{
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: { xs: '1rem', md: '1.1rem' },
          lineHeight: 1.6,
        }}>
          As fellow Bitcoiners, we understand the importance of tracking your sats alongside traditional assets. Get a complete view of your stack alongside the rest of your finances.
        </Typography>
      ),
      icon: <BitcoinIcon />,
    },
  ];

  return (
    <Box sx={{ 
      backgroundColor: colors.background,
      width: '100%',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'visible',
        backgroundColor: colors.background,
      }}>
        <HeroVideo
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </HeroVideo>
        <HeroOverlay />
        <Container maxWidth="lg" sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
            mb: 3,
            position: 'relative',
            zIndex: 2,
          }}>
            <img 
              src="/images/budgit-logo-md.png" 
              alt="Budgit Logo" 
              style={{ 
                height: '80px',
                width: 'auto',
              }} 
            />
            <Typography variant="h1" aria-label="Budgit – AI Budgeting App That Rewards Smart Spending" sx={{
              fontSize: { xs: '4rem', md: '6rem' },
              fontWeight: 900,
              color: colors.primary,
              letterSpacing: '-0.02em',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}>
              Budgit
            </Typography>
          </Box>
          <Typography variant="h2" sx={{
            fontSize: { xs: '1.5rem', md: '2rem' },
            fontWeight: 500,
            color: 'white',
            mb: 6,
            maxWidth: '600px',
            position: 'relative',
            zIndex: 2,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
          }}>
            Do more with your money.
          </Typography>
          {!user && pendingReferralCode && (
            <Box sx={{ 
              mb: 2, 
              p: 1.5, 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: 2, 
              border: '1px solid rgba(255, 255, 255, 0.2)',
              position: 'relative', 
              zIndex: 2 
            }}>
              <Typography variant="body2" sx={{ 
                color: colors.white, 
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                🎉 You have a referral code! Sign up to claim your benefits.
              </Typography>
            </Box>
          )}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            mb: 4,
            position: 'relative',
            zIndex: 2,
          }}>
            <LandingCtas tone="onDark" onWebSignup={openWebSignup} onStoreDownload={openStoreDownload} />
          </Box>

          {/* QR Code */}
          <Box sx={{ mt: 4, position: 'relative', zIndex: 2 }}>
            <img src="/images/budgit-qr-code.png" alt="QR Code" width="120" height="120" />
            <Typography variant="body2" sx={{ mt: 1, color: colors.white, textAlign: 'center' }}>
              Scan to download
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Floating QR */}
      {showFloatingQR && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'rgba(254, 242, 241, 0.9)',
            padding: 2,
            borderRadius: 2,
            boxShadow: 3,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <img src="/images/budgit-qr-dark.png" alt="QR Code" width="80" height="80" />
          <Typography variant="body2" sx={{ mt: 1, color: colors.secondary, fontSize: '0.8rem' }}>
            Download the app
          </Typography>
        </Box>
      )}

      {/* Learn More Arrow */}
      <Box
        ref={learnMoreRef}
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          zIndex: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.primary, mb: 1 }}>
          learn more
        </Typography>
        <KeyboardArrowDownIcon sx={{ color: colors.primary, fontSize: 30 }} />
      </Box>

      {/* Features Section */}
      <Box sx={{ position: 'relative', zIndex: 4 }}>
        <SlantedSection />
      </Box>

      {/* First Feature - Dictionary Entry */}
      <FeatureSection
        component={SlantedSection}
        sx={{ 
          backgroundColor: 'transparent',
          padding: 0,
          width: '100%',
          position: 'relative',
          zIndex: 4,
        }}
      >
        <Box sx={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: { 
            xs: '12rem 2rem 4rem 2rem', 
            md: '16rem 4rem 6rem 4rem' 
          },
          position: 'relative',
          zIndex: 1,
        }}>
          <DictionaryEntry sx={{ color: colors.white }}>
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 4 }}>
                <Word variant="h1" sx={{ color: colors.white }}>budgit</Word>
                <Pronunciation sx={{ color: colors.white }}>
                  [buh-jit]
                </Pronunciation>
              </Box>

              <Definition>
                <DefinitionNumber sx={{ color: colors.white }}>
                  1. verb (informal)
                </DefinitionNumber>
                <DefinitionText sx={{ color: colors.white }}>
                To manage money through small, consistent habits that build confidence and lasting change.
                </DefinitionText>
                <Example sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  "You don't need another reset. You just need to budgit."
                </Example>

                <DefinitionNumber sx={{ mt: 4, color: colors.white }}>
                  2. noun (proper)
                </DefinitionNumber>
                <DefinitionText sx={{ color: colors.white }}>
                  A personal finance app that helps you nudge your money in the right direction one decision at a time. Budgit turns budgeting into something practical and rewarding.
                </DefinitionText>
              </Definition>

              <Origin sx={{ borderColor: `${colors.white}20` }}>
                <Typography variant="h6" sx={{ color: colors.white, mb: 2, fontWeight: 600 }}>
                  Origin of budgit
                </Typography>
                <Typography sx={{ color: colors.white }}>
                  First recorded in 2025; from Late Modern English "budge" (to move or shift slightly or subtly) + "it" (the pronoun referring to "that thing you've been avoiding").
                </Typography>
              </Origin>
            </Box>
          </DictionaryEntry>
        </Box>
      </FeatureSection>

      {/* Remaining Features */}
      {features.map((feature, index) => (
        <FeatureSection
          key={index}
          component={feature.id === 'bitcoin' ? InvertedSlantedSection : undefined}
          sx={{
            backgroundColor: feature.id === 'bitcoin' ? 'transparent' :
                           feature.id === 'get-rewarded' ? colors.secondary :
                           feature.id === 'ai-budgeting' ? `${colors.primary}05` :
                           colors.white,
            padding: 0,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <FeatureBackground sx={{
            '&::before, &::after': {
              background: feature.id === 'get-rewarded' ? 'rgba(255, 255, 255, 0.1)' : colors.primary,
            }
          }} />
          <Box sx={{
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: { 
              xs: feature.id === 'bitcoin' ? '12rem 2rem 4rem 2rem' : '4rem 2rem',
              md: feature.id === 'bitcoin' ? '16rem 4rem 6rem 4rem' : '6rem 4rem' 
            },
            position: 'relative',
            zIndex: 1,
          }}>
            <Grid container spacing={8} alignItems="center" direction={index % 2 === 0 ? 'row' : 'row-reverse'}>
              <Grid item xs={12} md={6} sx={{
                textAlign: { xs: 'center', md: 'left' },
                position: 'relative',
              }}>
                <Typography variant="h3" component="h2" sx={{
                  mb: 3,
                  fontWeight: 800,
                  color: (feature.id === 'get-rewarded' || feature.id === 'bitcoin') ? 'white' : colors.primary,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  letterSpacing: '-0.02em',
                }}>
                  {feature.title}
                </Typography>
                {feature.description}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  {feature.mockup ? (
                    <DeviceMockup
                      src={feature.mockup}
                      alt={feature.mockupAlt}
                      loading="lazy"
                    />
                  ) : (
                    <Box sx={{
                      width: '100%',
                      maxWidth: '200px',
                      aspectRatio: '1',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: (feature.id === 'get-rewarded' || feature.id === 'bitcoin') ? 'rgba(255, 255, 255, 0.1)' : `${colors.primary}10`,
                      borderRadius: '24px',
                      '& svg': {
                        fontSize: '5rem',
                        color: (feature.id === 'get-rewarded' || feature.id === 'bitcoin') ? 'white' : colors.primary,
                      }
                    }}>
                      {feature.icon}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </FeatureSection>
      ))}

      {/* Footer */}
      <Box sx={{ 
        backgroundColor: colors.background,
        borderTop: `1px solid ${colors.primary}20`,
        pt: 8,
        pb: 4,
        px: { xs: 2, md: 4 }
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Download Section */}
            <Grid item xs={12} sx={{ mb: 6, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: 4,
                p: 4,
                backgroundColor: `${colors.primary}05`,
                borderRadius: 4,
                width: '100%',
                maxWidth: 'md',
              }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', sm: 'flex-start' },
                  flex: 1,
                }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 800,
                    color: colors.primary,
                    mb: 2,
                    textAlign: { xs: 'center', sm: 'left' }
                  }}>
                    Ready to get moving?
                  </Typography>
                  <LandingCtas tone="onLight" onWebSignup={openWebSignup} onStoreDownload={openStoreDownload} />
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                  <img src="/images/budgit-qr-dark.png" alt="QR Code" width="120" height="120" />
                  <Typography variant="body2" sx={{ mt: 1, color: colors.secondary }}>
                    Scan to download
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Social Media */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: colors.primary }}>
                Follow Our Journey
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {[
                  { 
                    icon: <XIcon />, 
                    name: 'X', 
                    href: 'https://x.com/thebudgitapp'
                  }
                ].map((item) => (
                  <Link
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      textDecoration: 'none',
                    }}
                  >
                    <Box
                      sx={{ 
                        cursor: 'pointer',
                        color: colors.secondary,
                        '&:hover': { 
                          color: colors.primary,
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        '& svg': {
                          fontSize: 20
                        }
                      }}
                    >
                      {item.icon}
                    </Box>
                  </Link>
                ))}
              </Box>
              <Typography variant="body2" sx={{ color: colors.secondary }}>
                Follow us on X (Twitter) for product updates, financial tips, and early access to new features.
              </Typography>
            </Grid>

            {/* Contact Us */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: colors.primary }}>
                Contact Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: colors.secondary }}>
                  support@budgit.app
                </Typography>
                <Typography variant="body2" sx={{ color: colors.secondary }}>
                  Mon-Fri: 9am-5pm PST
                </Typography>
              </Box>
            </Grid>

            {/* Disclaimer */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: colors.primary }}>
                Legal
              </Typography>
              <Typography variant="body2" sx={{ color: colors.secondary, mb: 2, lineHeight: 1.6, fontSize: '0.8rem', textAlign: 'justify' }}>
                Budgit provides general information to help you manage your finances. This information should not be construed as professional financial advice. Always consult a qualified financial advisor for personalized guidance.
              </Typography>
              <Typography variant="body2" sx={{ color: colors.secondary, mb: 2, lineHeight: 1.6, fontSize: '0.8rem', textAlign: 'justify' }}>
                Our AI insights and automatic categorizations are provided "as-is" and may contain inaccuracies. Please verify your data and consult professionals where necessary. Results may vary, and savings or rewards are not guaranteed.
              </Typography>
              <Typography variant="body2" sx={{ color: colors.secondary, mb: 2, lineHeight: 1.6, fontSize: '0.8rem', textAlign: 'justify' }}>
                Budgit relies on third-party providers (e.g., Plaid) for certain financial data and transactions. We are not liable for errors, delays, or outages caused by these providers. While we use industry-standard security measures, no method of transmission or storage is completely secure.
              </Typography>
              <Typography variant="body2" sx={{ color: colors.secondary, mb: 2, lineHeight: 1.6, fontSize: '0.8rem', textAlign: 'justify' }}>
                Users are fully responsible for their financial decisions. Use of Budgit and reliance on any insights or AI-driven suggestions is solely at your own risk. Budgit is not liable for any direct or indirect damages.
              </Typography>
              <Typography variant="body2" sx={{ color: colors.secondary, lineHeight: 1.6, fontSize: '0.8rem' }}>
                <a href="/pricing" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 700 }}>Pricing</a>
                {' · '}
                <a href="/privacy" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 700 }}>Privacy Policy</a>
                {' · '}
                <a href="/terms" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 700 }}>Terms of Service</a>
                {' · '}
                <button
                  type="button"
                  onClick={reopenCookieChoicesWithReloadWarning}
                  style={{
                    color: colors.primary,
                    background: 'none',
                    border: 0,
                    padding: 0,
                    font: 'inherit',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Cookie choices
                </button>
              </Typography>
            </Grid>

            {/* Copyright */}
            <Grid item xs={12} sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="caption" sx={{ color: colors.secondary, opacity: 0.8 }}>
                © {new Date().getFullYear()} Bleau Consulting LLC d/b/a Budgit. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {isLoginOpen !== undefined && (
        <Login open={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
      )}
    </Box>
  );
};

export default Welcome;
